import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Tabs,
    Tab,
    LinearProgress,
    CircularProgress,
    Snackbar,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import Loading from "../../components/Loading";
import ExportPrepReportModal from "./ExportPrepReportModal";
import GeneratePrepReportsModal from "./GeneratePrepReportsModal";
import PrepCalendar from "./PrepCalendar";
import PrepExamMatrix from "./PrepExamMatrix";
import PrepHomeworkMatrix from "./PrepHomeworkMatrix";
import PrepV2TestMatrix from "./PrepV2TestMatrix";
import { studentLabel, enrollmentEndedLabel } from "./prepStudentHeader";
import { LINKAGE } from "./prepUi";
import { programScheduleEvents, studentOverlayEvents, subjectLabelMap, earliestEventDate } from "./prepCalendarEvents";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

const PERIOD_OPTIONS = [
    { value: "all", label: "All-time" },
    { value: "current_round", label: "Current round" },
    { value: "this_week", label: "This week" },
    { value: "last_week", label: "Last week" },
    { value: "custom", label: "Custom range" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);
// Monday-based week start for a given date.
const weekStart = (d) => {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - day);
    return x;
};

// Map the UI period selection to the report endpoint's query params.
function periodParams(periodUi, customFrom, customTo) {
    const now = new Date();
    if (periodUi === "all") return { period: "all" };
    if (periodUi === "current_round") return { period: "current_round" };
    if (periodUi === "this_week") return { period: "custom", from: isoDay(weekStart(now)), to: isoDay(now) };
    if (periodUi === "last_week") {
        const start = weekStart(now);
        const lastStart = new Date(start);
        lastStart.setDate(lastStart.getDate() - 7);
        const lastEnd = new Date(start);
        lastEnd.setDate(lastEnd.getDate() - 1);
        return { period: "custom", from: isoDay(lastStart), to: isoDay(lastEnd) };
    }
    return { period: "custom", from: customFrom || undefined, to: customTo || undefined };
}

// Human-readable summary of the ETT sync the "Validate linkage" button triggers (see the
// validate-linkage route): exams are pushed inline (or time out → finishing in the background),
// homework rounds are queued for the worker.
function formatSyncMsg(sync) {
    const s = (n) => (n === 1 ? "" : "s");
    const e = sync.exams || {};
    const h = sync.homework || {};
    if (e.skipped && h.skipped) return "No linked students — nothing to sync to ETT.";
    const parts = [];
    if (e.timeout) parts.push("exams syncing in background…");
    else parts.push(`exams synced for ${e.synced || 0} student${s(e.synced || 0)}${e.failed ? `, ${e.failed} unlinked/failed` : ""}`);
    if (!h.skipped) parts.push(`${h.rounds_queued || 0} homework round${s(h.rounds_queued || 0)} queued`);
    return `ETT sync: ${parts.join(" · ")}`;
}

const PrepProgramDashboard = () => {
    const { uuid } = useParams();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();

    const [programName, setProgramName] = useState("");
    const [courses, setCourses] = useState([]); // [{uuid, course_title}] for the export modal
    const [exams, setExams] = useState([]);     // program schedule for the calendar
    const [rounds, setRounds] = useState([]);
    const [subjectLabels, setSubjectLabels] = useState({});
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(false);
    const [syncMsg, setSyncMsg] = useState(null); // toast after "Validate linkage" pushes work to ETT
    const [tab, setTab] = useState(0); // 0 = Practice Exams, 1 = QBank Homework, 2 = Library Tests, 3 = Students, 4 = Schedule

    const [periodUi, setPeriodUi] = useState("all");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const [selected, setSelected] = useState(null); // selected student row
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    // Program-level manual report generation (hand-picked students/tests/rounds), distinct from
    // the per-student Export PDF below, which follows the dashboard's period selector.
    const [generateOpen, setGenerateOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0); // bump to remount/refetch the matrices

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };
    const auth = { headers: { Authorization: getAuthHeader() } };
    const openClassroom = (uuid) => window.open(`/classrooms/${uuid}/home`, "_blank", "noopener");

    const load = useCallback(async () => {
        try {
            const [detailRes, studentsRes] = await Promise.all([
                Axios.get(`${BASE}/${uuid}`, auth),
                Axios.get(`${BASE}/${uuid}/enrolled-students`, auth),
            ]);
            setProgramName(detailRes.data.program?.name || "Program");
            setCourses(
                (detailRes.data.courses || [])
                    .map((c) => c.course)
                    .filter((c) => c && c.uuid)
                    .filter((c, i, arr) => arr.findIndex((x) => x.uuid === c.uuid) === i)
            );
            setExams(detailRes.data.exams || []);
            setRounds(detailRes.data.rounds || []);
            setSubjectLabels(subjectLabelMap((detailRes.data.courses || []).filter((c) => c.role === "SUBJECT")));
            setStudents(studentsRes.data || []);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else console.error("Dashboard load error:", error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    useEffect(() => {
        localStorage.setItem("selectedMenu", "prepPrograms");
        load();
    }, [load]);

    const loadReport = useCallback(async (student, periodUiArg) => {
        if (!student) return;
        setReportLoading(true);
        setReport(null);
        try {
            const params = periodParams(periodUiArg, customFrom, customTo);
            const res = await Axios.get(`${BASE}/${uuid}/students/${student.student_id}/report`, { ...auth, params });
            setReport(res.data);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else console.error("Report load error:", error);
        } finally {
            setReportLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid, customFrom, customTo]);

    const handleSelect = (student) => {
        setSelected(student);
        loadReport(student, periodUi);
    };

    const handlePeriodChange = (value) => {
        setPeriodUi(value);
        if (selected && value !== "custom") loadReport(selected, value);
    };

    const syncProgress = async () => {
        setSyncing(true);
        try {
            await Axios.post(`${BASE}/${uuid}/sync-progress`, {}, auth);
            setRefreshKey((k) => k + 1); // refetch the matrices with fresh counts
            if (selected) loadReport(selected, periodUi); // and the open student report
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else console.error("Sync progress error:", error);
        } finally {
            setSyncing(false);
        }
    };

    const validateLinkage = async () => {
        setValidating(true);
        try {
            const res = await Axios.post(`${BASE}/${uuid}/validate-linkage`, {}, auth);
            // Response is now { students, sync }; tolerate the legacy bare-array shape too.
            const fresh = Array.isArray(res.data) ? res.data : (res.data.students || []);
            setStudents(fresh);
            // Keep the open detail panel's linkage in sync with the refreshed list.
            if (selected) {
                const updated = fresh.find((s) => s.student_id === selected.student_id);
                if (updated) setSelected(updated);
            }
            // Report what was pushed to ETT for students who linked since release, and refetch the
            // matrices so the newly-synced exams (and queued homework) show up.
            const sync = Array.isArray(res.data) ? null : res.data.sync;
            if (sync) setSyncMsg(formatSyncMsg(sync));
            setRefreshKey((k) => k + 1);
        } catch (error) {
            console.error("Validate linkage error:", error);
            setSyncMsg("Linkage re-check failed — see console.");
        } finally {
            setValidating(false);
        }
    };

    if (loading) return <Loading />;

    const plural = (n) => (n === 1 ? "" : "s");
    // Departed students stay on the roster for their history, but they are not a linkage problem
    // to fix — a student who left will legitimately have no active ETT plan. Warn on active only.
    const activeStudents = students.filter((s) => s.enrollment_status !== "ended");
    const unlinkedCount = activeStudents.filter((s) => ["email_unresolved", "no_ett_email"].includes(s.linkage?.status)).length;
    const noPlanCount = activeStudents.filter((s) => s.linkage?.status === "no_active_plan").length;
    const allGood = unlinkedCount === 0 && noPlanCount === 0;
    const linkageSummary = allGood
        ? "All students linked and have plans"
        : [
              unlinkedCount ? `${unlinkedCount} student${plural(unlinkedCount)} unlinked` : null,
              noPlanCount ? `${noPlanCount} student${plural(noPlanCount)} without an active plan` : null,
          ].filter(Boolean).join(" · ");

    // Calendar: program schedule always; when a student is selected, hand the
    // homework-due markers to the per-student overlay (with their mastery progress)
    // and add the tests they've actually taken.
    const calendarEvents = [
        ...programScheduleEvents({ exams, rounds, subjectLabels, includeHwDue: !selected }),
        ...(selected && report
            ? studentOverlayEvents({ scores: report.scores, assignments: report.assignments, subjectLabels })
            : []),
    ];

    return (
        <Box m="20px">
            <Box display="flex" alignItems="center" gap={1} mb="16px">
                <IconButton onClick={() => navigate("/admin/prepPrograms")}><ArrowBackIcon /></IconButton>
                <Typography variant="h4" fontWeight="bold">{programName} — Dashboard</Typography>
                <Box flexGrow={1} />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={syncProgress}
                    disabled={syncing}
                    startIcon={syncing ? <CircularProgress size={16} /> : null}
                >
                    {syncing ? "Syncing…" : "Sync progress"}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setGenerateOpen(true)}>
                    Generate reports
                </Button>
                <FormControl size="small" sx={{ width: 170 }}>
                    <InputLabel>Period</InputLabel>
                    <Select label="Period" value={periodUi} onChange={(e) => handlePeriodChange(e.target.value)}>
                        {PERIOD_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                    </Select>
                </FormControl>
                {periodUi === "custom" && (
                    <>
                        <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
                            value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                        <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }}
                            value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                        <Button size="small" variant="outlined" disabled={!selected} onClick={() => selected && loadReport(selected, "custom")}>
                            Apply
                        </Button>
                    </>
                )}
            </Box>

            {courses.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                    {courses.map((c) => (
                        <Chip
                            key={c.uuid}
                            size="small"
                            variant="outlined"
                            clickable
                            label={c.course_title}
                            onClick={() => openClassroom(c.uuid)}
                        />
                    ))}
                </Box>
            )}

            {students.length > 0 && (
                <Alert severity={allGood ? "success" : "warning"} sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={validateLinkage} disabled={validating}
                            startIcon={validating ? <CircularProgress size={16} /> : null}>
                            Validate linkage
                        </Button>
                    }>
                    {linkageSummary}
                </Alert>
            )}

            <Snackbar
                open={!!syncMsg}
                autoHideDuration={6000}
                onClose={() => setSyncMsg(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="info" onClose={() => setSyncMsg(null)} sx={{ width: "100%" }}>
                    {syncMsg}
                </Alert>
            </Snackbar>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
                <Tab label="Practice Exams" />
                <Tab label="QBank Homework" />
                <Tab label="Library Tests" />
                {/* Count ACTIVE students — the roster below also lists departed ones (dimmed), but
                    the headline number must mean "current headcount", same as the linkage alert. */}
                <Tab label={`Students${students.length ? ` (${activeStudents.length}${students.length > activeStudents.length ? ` + ${students.length - activeStudents.length} ended` : ""})` : ""}`} />
                <Tab label="Schedule" />
            </Tabs>

            {/* Practice-exam matrix (default tab): exams × students with scores/status */}
            {tab === 0 && (
                <Box sx={{ mb: 3 }}>
                    <PrepExamMatrix key={refreshKey} uuid={uuid} />
                </Box>
            )}

            {/* QBank-homework matrix: rounds → chapters × students */}
            {tab === 1 && (
                <Box sx={{ mb: 3 }}>
                    <PrepHomeworkMatrix key={refreshKey} uuid={uuid} />
                </Box>
            )}

            {/* Library-test (v2) matrix: vocab & other Test-Library tests × students */}
            {tab === 2 && (
                <Box sx={{ mb: 3 }}>
                    <PrepV2TestMatrix key={refreshKey} uuid={uuid} />
                </Box>
            )}

            {/* Students roster (Students tab) */}
            {tab === 3 && (
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600], mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Student</TableCell>
                            <TableCell>Subjects</TableCell>
                            <TableCell>Linkage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {students.map((s) => {
                            const lk = LINKAGE[s.linkage?.status] || { label: s.linkage?.status || "unknown", color: "default" };
                            return (
                                <TableRow key={s.student_id} hover selected={selected?.student_id === s.student_id}
                                    sx={{ cursor: "pointer" }} onClick={() => handleSelect(s)}>
                                    <TableCell sx={s.enrollment_status === "ended" ? { color: "text.disabled" } : undefined}>
                                        {studentLabel(s)}
                                        {s.enrollment_status === "ended" && (
                                            <Chip size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }}
                                                label={enrollmentEndedLabel(s)} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(s.subjects || []).map((sub, i) => (
                                            <Chip key={i} size="small" sx={{ mr: 0.5 }}
                                                label={sub.label || sub.course_title || sub.role} />
                                        ))}
                                    </TableCell>
                                    <TableCell><Chip size="small" color={lk.color} label={lk.label} /></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            )}

            {/* Schedule calendar (Schedule tab) — program-wide, overlaid with the selected student */}
            {tab === 4 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        Schedule
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                        {selected
                            ? `Showing ${selected.name || selected.email}'s completions and homework progress over the program schedule.`
                            : "Program schedule — select a student in the Students tab to overlay their completions."}
                    </Typography>
                    {calendarEvents.length > 0 ? (
                        <PrepCalendar
                            events={calendarEvents}
                            initialDate={earliestEventDate(calendarEvents)}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">No schedule items yet.</Typography>
                    )}
                </Paper>
            )}

            {/* Per-student detail (Students tab) */}
            {tab === 3 && selected && (
                <Paper sx={{ p: 2, backgroundColor: colors.primary[600] }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        {/* Same ended treatment as the roster row — otherwise an empty homework
                            table under a plain bold name reads as "did nothing" instead of "left". */}
                        <Typography variant="h5" fontWeight="bold"
                            sx={selected.enrollment_status === "ended" ? { color: "text.disabled" } : undefined}>
                            {studentLabel(selected)}
                        </Typography>
                        {selected.enrollment_status === "ended" && (
                            <Chip size="small" variant="outlined" sx={{ ml: 1.5, height: 20, fontSize: 11 }}
                                label={enrollmentEndedLabel(selected)} />
                        )}
                        <Box flexGrow={1} />
                        <Button variant="contained" size="small" disabled={reportLoading} onClick={() => setExportOpen(true)}>
                            Export PDF
                        </Button>
                    </Box>
                    {selected.linkage?.status !== "linked" && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This student is not fully linked ({LINKAGE[selected.linkage?.status]?.label}). Report data may be limited.
                        </Alert>
                    )}

                    {reportLoading ? (
                        <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                    ) : report ? (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Practice Test Scores</Typography>
                            {(report.scores || []).length === 0 ? (
                                <Typography variant="body2" color={colors.grey[300]}>No practice-test scores.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead><TableRow>
                                        <TableCell>Test</TableCell><TableCell>Date</TableCell><TableCell>Score</TableCell><TableCell>Detail</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>
                                        {report.scores.map((s, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{s.test_title}</TableCell>
                                                <TableCell>{s.finished_at ? new Date(s.finished_at).toLocaleDateString() : "—"}</TableCell>
                                                <TableCell>{s.score_total}</TableCell>
                                                <TableCell>{s.detail}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}

                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Question-Bank Homework ({report.period})</Typography>
                            {(report.assignments || []).length === 0 ? (
                                <Typography variant="body2" color={colors.grey[300]}>No homework in this period.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead><TableRow>
                                        <TableCell>Chapter</TableCell><TableCell sx={{ width: 220 }}>Progress</TableCell>
                                        <TableCell>Minutes</TableCell><TableCell>Status</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>
                                        {report.assignments.map((a) => {
                                            // Use the server's per-round count (what the bar is computed from) so the number
                                            // and the bar agree; fall back to the baseline delta only if it's absent.
                                            const done = a.solved_this_round ?? Math.max(0, (a.questions_solved || 0) - (a.baseline_solved || 0));
                                            const pct = a.completion_pct ?? 0;
                                            return (
                                                <TableRow key={`${a.round_id}-${a.chapter_id}`}>
                                                    <TableCell>{a.chapter_name || `Chapter ${a.chapter_id}`}</TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <LinearProgress variant="determinate" value={Math.min(100, pct)} />
                                                            </Box>
                                                            <Typography variant="caption">{done}/{a.target_questions ?? "—"}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{a.minutes_spent ?? 0}</TableCell>
                                                    <TableCell>{a.mastered ? <Chip size="small" color="success" label={`${a.mastery_bar ?? report.mastery_threshold ?? 100}+ practiced`} /> : `${pct}%`}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color={colors.grey[300]}>Could not load this student's report.</Typography>
                    )}
                </Paper>
            )}

            <GeneratePrepReportsModal
                open={generateOpen}
                onClose={() => setGenerateOpen(false)}
                programUuid={uuid}
                programName={programName}
            />

            <ExportPrepReportModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                programUuid={uuid}
                programName={programName}
                student={selected}
                courses={courses}
                periodSel={periodParams(periodUi, customFrom, customTo)}
            />
        </Box>
    );
};

export default PrepProgramDashboard;
